import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  getFiscalYears, createFiscalYear, closeFiscalYear, updatePeriod,
} from "../../api/accountingApi";

const FiscalYearPage = () => {
  const toast = useRef(null);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [expandedRows, setExpandedRows] = useState(null);
  const [form, setForm] = useState({ name: "", start_date: null, end_date: null });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadFiscalYears();
  }, []);

  const loadFiscalYears = async () => {
    try {
      setLoading(true);
      const data = await getFiscalYears();
      setFiscalYears(data);
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load fiscal years" });
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    const now = new Date();
    setForm({
      name: `FY ${now.getFullYear()}-${now.getFullYear() + 1}`,
      start_date: new Date(now.getFullYear(), 0, 1),
      end_date: new Date(now.getFullYear(), 11, 31),
    });
    setDialogVisible(true);
  };

  const handleCreate = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "All fields are required" });
      return;
    }

    try {
      setSaving(true);
      await createFiscalYear({
        name: form.name,
        start_date: form.start_date.toISOString().split("T")[0],
        end_date: form.end_date.toISOString().split("T")[0],
      });
      toast.current?.show({ severity: "success", summary: "Success", detail: "Fiscal year created with monthly periods" });
      setDialogVisible(false);
      loadFiscalYears();
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.message || "Failed to create fiscal year",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (fy) => {
    confirmDialog({
      message: `Are you sure you want to close "${fy.name}"? All open periods will be closed. This cannot be undone.`,
      header: "Close Fiscal Year",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await closeFiscalYear(fy.id);
          toast.current?.show({ severity: "success", summary: "Success", detail: "Fiscal year closed" });
          loadFiscalYears();
        } catch (err) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: err.response?.data?.message || "Failed to close fiscal year",
          });
        }
      },
    });
  };

  const handlePeriodStatus = async (period, newStatus) => {
    try {
      await updatePeriod(period.id, { status: newStatus });
      toast.current?.show({ severity: "success", summary: "Success", detail: `Period ${newStatus}` });
      loadFiscalYears();
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.message || "Failed to update period",
      });
    }
  };

  const statusTemplate = (rowData) => {
    const colors = { open: "success", closed: "danger" };
    return <Tag value={rowData.status} severity={colors[rowData.status]} />;
  };

  const periodCountTemplate = (rowData) => {
    const periods = rowData.periods || [];
    const openCount = periods.filter((p) => p.status === "open").length;
    return `${openCount}/${periods.length} open`;
  };

  const actionsTemplate = (rowData) => (
    <div className="flex gap-1">
      {rowData.status === "open" && (
        <Button
          icon="pi pi-lock"
          label="Close"
          className="p-button-sm p-button-danger p-button-outlined"
          onClick={() => handleClose(rowData)}
        />
      )}
    </div>
  );

  const periodStatusTemplate = (period) => {
    const colors = { open: "success", closed: "warning", locked: "danger" };
    return <Tag value={period.status} severity={colors[period.status]} />;
  };

  const periodActionsTemplate = (period) => {
    const statusOptions = [
      { label: "Open", value: "open" },
      { label: "Closed", value: "closed" },
      { label: "Locked", value: "locked" },
    ];

    if (period.status === "locked") return <span className="text-500">Locked</span>;

    return (
      <Dropdown
        value={period.status}
        options={statusOptions}
        onChange={(e) => handlePeriodStatus(period, e.value)}
        className="p-inputtext-sm"
        style={{ width: "120px" }}
      />
    );
  };

  const rowExpansionTemplate = (data) => {
    const periods = data.periods || [];
    return (
      <div className="p-3">
        <h4 className="mt-0 mb-2">Fiscal Periods</h4>
        <DataTable value={periods} size="small" emptyMessage="No periods">
          <Column field="period_number" header="#" style={{ width: "4rem" }} />
          <Column field="name" header="Period" />
          <Column field="start_date" header="Start Date" />
          <Column field="end_date" header="End Date" />
          <Column header="Status" body={periodStatusTemplate} style={{ width: "8rem" }} />
          <Column header="Action" body={periodActionsTemplate} style={{ width: "10rem" }} />
        </DataTable>
      </div>
    );
  };

  const leftToolbar = () => (
    <span className="text-500">
      {fiscalYears.length} fiscal year(s)
    </span>
  );

  const rightToolbar = () => (
    <Button label="New Fiscal Year" icon="pi pi-plus" className="p-button-sm p-button-success" onClick={openNew} />
  );

  if (loading && fiscalYears.length === 0) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: "60vh" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="mb-3">
        <h2 className="m-0">Fiscal Years</h2>
        <p className="text-500 mt-1 mb-0">Manage fiscal years and periods for accounting</p>
      </div>

      <Toolbar className="mb-3" left={leftToolbar} right={rightToolbar} />

      <div className="card">
        <DataTable
          value={fiscalYears}
          loading={loading}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
          emptyMessage="No fiscal years found. Create one to get started."
          className="p-datatable-sm"
        >
          <Column expander style={{ width: "3rem" }} />
          <Column field="name" header="Name" sortable />
          <Column field="start_date" header="Start Date" sortable />
          <Column field="end_date" header="End Date" sortable />
          <Column header="Status" body={statusTemplate} style={{ width: "8rem" }} />
          <Column header="Periods" body={periodCountTemplate} style={{ width: "8rem" }} />
          <Column header="Actions" body={actionsTemplate} style={{ width: "10rem" }} />
        </DataTable>
      </div>

      <Dialog
        header="New Fiscal Year"
        visible={dialogVisible}
        style={{ width: "450px" }}
        modal
        onHide={() => setDialogVisible(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" className="p-button-text" onClick={() => setDialogVisible(false)} />
            <Button label="Create" icon="pi pi-check" onClick={handleCreate} loading={saving} />
          </div>
        }
      >
        <div className="flex flex-column gap-3 pt-2">
          <div className="flex flex-column gap-1">
            <label htmlFor="fy-name">Name *</label>
            <InputText
              id="fy-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. FY 2025-2026"
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="fy-start">Start Date *</label>
            <Calendar
              id="fy-start"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.value })}
              dateFormat="yy-mm-dd"
              showIcon
            />
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="fy-end">End Date *</label>
            <Calendar
              id="fy-end"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.value })}
              dateFormat="yy-mm-dd"
              showIcon
            />
          </div>
          <p className="text-500 text-sm m-0">
            12 monthly periods will be automatically generated within the date range.
          </p>
        </div>
      </Dialog>

      <ConfirmDialog />
      <Toast ref={toast} />
    </div>
  );
};

export default FiscalYearPage;
